import apiClient from '../client';
import type { Payment, PaginatedResponse, PaymentStatsResponse } from '../../types';

interface PageParams {
  page?: number;
  size?: number;
}

export interface SalaryPaymentRequest {
  employeeId: string;
  amount: number;
  periodMonth: number;
  periodYear: number;
  paymentDate: string;
  paymentMethod: string;
  referenceNumber?: string;
  notes?: string;
  farmOperationId?: string;
  operationTaskId?: string;
}

export interface BulkSalaryPaymentRequest {
  periodMonth: number;
  periodYear: number;
  paymentDate: string;
  paymentMethod: string;
  notes?: string;
  employeeIds?: string[];
}

export interface ExpensePaymentRequest {
  expenseRequestId: string;
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  referenceNumber?: string;
  notes?: string;
}

export interface ContractorPaymentRequest {
  contractorId?: string;
  contractorName?: string;
  contractorContact?: string;
  description?: string;
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  referenceNumber?: string;
  notes?: string;
  farmOperationId?: string;
  operationTaskId?: string;
}

export interface ProcurementPaymentRequest {
  procurementItem: string;
  procurementItemCustom?: string;
  supplierName?: string;
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  referenceNumber?: string;
  notes?: string;
  farmOperationId?: string;
  operationTaskId?: string;
}

export interface DisburseRequest {
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  referenceNumber?: string;
  notes?: string;
}

export interface UpdatePaymentAmountRequest {
  amount: number;
  reason?: string;
}

const paymentService = {
  getPayments: async (params: PageParams = {}) => {
    const { page = 0, size = 20 } = params;
    const response = await apiClient.get<PaginatedResponse<Payment>>(
      `/payments?page=${page}&size=${size}&sort=paymentDate,desc`
    );
    return response.data;
  },

  getPaymentsByType: async (type: string, params: PageParams = {}) => {
    const { page = 0, size = 20 } = params;
    const response = await apiClient.get<PaginatedResponse<Payment>>(
      `/payments/type/${type}?page=${page}&size=${size}&sort=paymentDate,desc`
    );
    return response.data;
  },

  getPaymentById: async (id: string) => {
    const response = await apiClient.get<Payment>(`/payments/${id}`);
    return response.data;
  },

  getEmployeePaymentHistory: async (employeeId: string, params: PageParams = {}) => {
    const { page = 0, size = 20 } = params;
    const response = await apiClient.get<PaginatedResponse<Payment>>(
      `/payments/employee/${employeeId}?page=${page}&size=${size}&sort=paymentDate,desc`
    );
    return response.data;
  },

  getSalaryPaymentsByYear: async (employeeId: string, year: number) => {
    const response = await apiClient.get<Payment[]>(`/payments/employee/${employeeId}/year/${year}`);
    return response.data;
  },

  getByProject: async (projectId: string, params: PageParams = {}) => {
    const { page = 0, size = 10 } = params;
    const response = await apiClient.get<PaginatedResponse<Payment>>(
      `/payments/project/${projectId}?page=${page}&size=${size}&sort=paymentDate,desc`
    );
    return response.data;
  },

  getByFarmOperation: async (farmOperationId: string, params: PageParams = {}) => {
    const { page = 0, size = 10 } = params;
    const response = await apiClient.get<PaginatedResponse<Payment>>(
      `/payments/operation/${farmOperationId}?page=${page}&size=${size}&sort=paymentDate,desc`
    );
    return response.data;
  },

  getByOperationTask: async (operationTaskId: string, params: PageParams = {}) => {
    const { page = 0, size = 10 } = params;
    const response = await apiClient.get<PaginatedResponse<Payment>>(
      `/payments/task/${operationTaskId}?page=${page}&size=${size}&sort=paymentDate,desc`
    );
    return response.data;
  },

  getPaymentsByDateRange: async (startDate: string, endDate: string, params: PageParams = {}) => {
    const { page = 0, size = 20 } = params;
    const response = await apiClient.get<PaginatedResponse<Payment>>(
      `/payments/date-range?startDate=${startDate}&endDate=${endDate}&page=${page}&size=${size}`
    );
    return response.data;
  },

  processSalaryPayment: async (data: SalaryPaymentRequest) => {
    const response = await apiClient.post<Payment>('/payments/salary', data);
    return response.data;
  },

  processBulkSalaryPayments: async (data: BulkSalaryPaymentRequest) => {
    const response = await apiClient.post<Payment[]>('/payments/salary/bulk', data);
    return response.data;
  },

  processPayroll: async () => {
    await apiClient.post('/payments/payroll');
  },

  processExpensePayment: async (data: ExpensePaymentRequest) => {
    const response = await apiClient.post<Payment>('/payments/expense', data);
    return response.data;
  },

  processContractorPayment: async (data: ContractorPaymentRequest) => {
    const response = await apiClient.post<Payment>('/payments/contractor', data);
    return response.data;
  },

  processProcurementPayment: async (data: ProcurementPaymentRequest) => {
    const response = await apiClient.post<Payment>('/payments/procurement', data);
    return response.data;
  },

  disburseContractorPayment: async (requestId: string, data: DisburseRequest) => {
    const response = await apiClient.post<Payment>(`/payments/disburse/${requestId}`, data);
    return response.data;
  },

  disburseProcurementPayment: async (requestId: string, data: DisburseRequest) => {
    const response = await apiClient.post<Payment>(`/payments/disburse-procurement/${requestId}`, data);
    return response.data;
  },

  cancelPayment: async (id: string) => {
    await apiClient.delete(`/payments/${id}`);
  },

  updatePaymentAmount: async (id: string, data: UpdatePaymentAmountRequest) => {
    const response = await apiClient.put<Payment>(`/payments/${id}/amount`, data);
    return response.data;
  },

  moveToProject: async (id: string, targetProjectId: string) => {
    const response = await apiClient.patch<Payment>(`/payments/${id}/move-to-project?targetProjectId=${targetProjectId}`);
    return response.data;
  },

  moveToOperation: async (id: string, targetFarmOperationId: string) => {
    const response = await apiClient.patch<Payment>(`/payments/${id}/move-to-operation?targetFarmOperationId=${targetFarmOperationId}`);
    return response.data;
  },

  getStats: async (params: { search?: string; type?: string; projectId?: string; farmOperationId?: string; operationTaskId?: string } = {}) => {
    const { search, type, projectId, farmOperationId, operationTaskId } = params;
    const qs = new URLSearchParams();
    if (search) qs.set('search', search);
    if (type) qs.set('type', type);
    if (projectId) qs.set('projectId', projectId);
    if (farmOperationId) qs.set('farmOperationId', farmOperationId);
    if (operationTaskId) qs.set('operationTaskId', operationTaskId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const response = await apiClient.get<PaymentStatsResponse>(`/payments/stats${suffix}`);
    return response.data;
  },

  search: async (params: PageParams & { search?: string; type?: string; projectId?: string; farmOperationId?: string; operationTaskId?: string } = {}) => {
    const { page = 0, size = 10, search, type, projectId, farmOperationId, operationTaskId } = params;
    const qs = new URLSearchParams({ page: page.toString(), size: size.toString() });
    if (search) qs.set('search', search);
    if (type) qs.set('type', type);
    if (projectId) qs.set('projectId', projectId);
    if (farmOperationId) qs.set('farmOperationId', farmOperationId);
    if (operationTaskId) qs.set('operationTaskId', operationTaskId);
    const response = await apiClient.get<PaginatedResponse<Payment>>(`/payments/search?${qs}`);
    return response.data;
  },
};

export default paymentService;
